import apiClient from "../../api/api";

/**
 * @description Fetches a compliance record by its unique identifier.
 *
 * @async
 * @function requestGetComplianceById
 * @param {number} complianceId - The unique identifier of the compliance record.
 *
 * @returns {Promise<any>} A promise that resolves with the compliance data.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestGetComplianceById = async(complianceId) => {
    const getComplianceReponse = await apiClient.get(import.meta.env.VITE_BASE_COMPLIANCE_URL+`/${complianceId}`)

    return getComplianceReponse.data
}

/**
 * @description Creates a new compliance record.
 *
 * @async
 * @function requestCreateCompliance
 * @param {Object} compliance - The compliance payload to be created.
 *
 * @returns {Promise<any>} A promise that resolves with the created compliance data.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestCreateCompliance = async(compliance) => {
    const getComplianceReponse = await apiClient.post(import.meta.env.VITE_BASE_COMPLIANCE_URL, compliance, {successToast:"Compliance created"})

    return getComplianceReponse.data
}

/**
 * @description Updates an existing compliance record.
 *
 * @async
 * @function requestUpdateCompliance
 * @param {number} complianceId - The unique identifier of the compliance record.
 * @param {Object} compliance - The payload containing the updated compliance data.
 *
 * @returns {Promise<any>} A promise that resolves with the update response.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestUpdateCompliance = async(complianceId, compliance) => {
    const getComplianceReponse = await apiClient.put(import.meta.env.VITE_BASE_COMPLIANCE_URL+`/${complianceId}`, compliance, {successToast:"Compliance updated"})

    return getComplianceReponse
}


/**
 * @description Deletes a compliance record by its unique identifier.
 *
 * @async
 * @function requestDeleteCompliance
 * @param {number} complianceId - The unique identifier of the compliance record.
 *
 * @returns {Promise<any>} A promise that resolves with the delete response data.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestDeleteCompliance = async(complianceId) => {
    const getComplianceReponse = await apiClient.delete(import.meta.env.VITE_BASE_COMPLIANCE_URL+`/${complianceId}`, {successToast:"Compliance deleted"})

    return getComplianceReponse.data
}

/**
 * @description Fetches compliances filtered by admin and HR.
 * Supports filtering by open or closed status.
 *
 * @async
 * @function requestGetCompliances
 * @param {number} managerId - The unique identifier of the admin and HR.
 * @param {boolean} isOpen - Indicates whether to fetch only open compliances.
 *
 * @returns {Promise<any>} A promise that resolves with the compliances list.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestGetCompliances = async(isOpen, projectId, page, row) => {
    const params = {
        isOpen,
        page,
        limit: row
    }
    
    if(projectId > 0) params.projectId = projectId

    const response = await apiClient.get(import.meta.env.VITE_BASE_GET_COMPLIANCE_BY_MANAGER, { params })

    return response.data
}

/**
 * @description Fetches compliances associated with a specific user.
 *
 * @async
 * @function requestGetCompliancesByUser
 * @param {number} userId - The unique identifier of the user.
 *
 * @returns {Promise<any>} A promise that resolves with the compliances list.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestGetCompliancesByUser = async(userId, page = 1, limit = 100) => {
    const params = {
        page,
        limit
    }
    const getCompliancesByUserResponse = await apiClient.get(import.meta.env.VITE_BASE_GET_COMPLIANCE_BY_USER+`/${userId}`, {params})

    return getCompliancesByUserResponse.data
}
